import { useMemo } from 'react';

import PublicEnv from 'shared/utils/public-env';
import { clearFederationManifestCache, ensureRemoteInitialized } from 'src/utils/module-federation/runtime-init';
import { getLogger } from 'src/utils/datadog-logger';

export const CONCIERGE_REMOTE_NAME = 'consumer_concierge';

// Remove once argocd has synced CONSUMER_CONCIERGE_MF_REMOTE_URL
export const LOCAL_FALLBACK_MANIFEST_URL = 'http://localhost:3201/mf-manifest.json';

const logger = getLogger('concierge-host');

type RemoteStatus = 'registered' | 'unavailable';

export function getConciergeManifestUrl(): string {
  return PublicEnv.consumerConciergeMfRemoteUrl || LOCAL_FALLBACK_MANIFEST_URL;
}

export function reinitializeConciergeRemote(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const mfInitialized = (window as Window & { __MF_INITIALIZED?: boolean }).__MF_INITIALIZED;
  if (!mfInitialized) {
    return false;
  }
  const manifestUrl = getConciergeManifestUrl();
  clearFederationManifestCache(manifestUrl);
  return ensureRemoteInitialized({
    name: CONCIERGE_REMOTE_NAME,
    manifestUrl,
    force: true,
  });
}

// useMemo (not useEffect) so the status is returned synchronously and the
// unavailable panel doesn't flash. Safe to re-run: MF's registerRemotes
// dedupes by name (warns and keeps the existing entry on repeat) and a
// duplicate log is harmless.
export function useConciergeRemoteRegistration(attempt = 0): RemoteStatus {
  return useMemo<RemoteStatus>(() => {
    void attempt;
    if (typeof window === 'undefined') {
      return 'unavailable';
    }
    const mfInitialized = (window as Window & { __MF_INITIALIZED?: boolean }).__MF_INITIALIZED;
    if (!mfInitialized) {
      logger.error('MF runtime not initialized; remote cannot load', {
        remote: CONCIERGE_REMOTE_NAME,
      });
      return 'unavailable';
    }
    const manifestUrl = PublicEnv.consumerConciergeMfRemoteUrl || LOCAL_FALLBACK_MANIFEST_URL;
    if (!PublicEnv.consumerConciergeMfRemoteUrl) {
      logger.warn('Using localhost fallback for concierge MF manifest URL', {
        remote: CONCIERGE_REMOTE_NAME,
        manifestUrl,
      });
      console.warn(
        `[concierge] CONSUMER_CONCIERGE_MF_REMOTE_URL is not set; falling back to ${LOCAL_FALLBACK_MANIFEST_URL}. This will only resolve in a local dev environment.`
      );
    }
    const registered = ensureRemoteInitialized({
      name: CONCIERGE_REMOTE_NAME,
      manifestUrl,
      force: attempt > 0,
    });
    if (!registered) {
      logger.error('Remote registration failed', {
        remote: CONCIERGE_REMOTE_NAME,
        manifestUrl,
      });
    }
    return registered ? 'registered' : 'unavailable';
  }, [attempt]);
}
